import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Button,
  Typography,
  Box,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { resetPasswordApi } from "../../../utils/api/auth";
import PasswordField from "../../../helpers/PasswordField";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(intl.formatMessage({ id: "signUp.passwordRequired" }))
      .min(8, intl.formatMessage({ id: "signUp.passwordMinLength" }))
      .matches(/[A-Z]/, intl.formatMessage({ id: "signUp.passwordUppercase" }))
      .matches(/[a-z]/, intl.formatMessage({ id: "signUp.passwordLowercase" }))
      .matches(/[0-9]/, intl.formatMessage({ id: "signUp.passwordNumber" }))
      .matches(
        /[@$!%*?&]/,
        intl.formatMessage({ id: "signUp.passwordSpecialChar" })
      ),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password")],
        intl.formatMessage({ id: "resetPassword.passwordsMustMatch" })
      )
      .required(
        intl.formatMessage({ id: "resetPassword.confirmPasswordRequired" })
      ),
  });

  const handleSubmit = async (
    values: { password: string; confirmPassword: string },
    { setSubmitting, setStatus }: any
  ) => {
    try {
      if (token) {
        await resetPasswordApi(token, values.password);
        setStatus({ success: true });
        setServerError(null);
        setTimeout(() => navigate("/auth/signin"), 3000);
      } else {
        throw new Error(
          intl.formatMessage({ id: "resetPassword.noTokenFound" })
        );
      }
    } catch (error: any) {
      setStatus({ success: false });
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setServerError(
              intl.formatMessage({ id: "resetPassword.invalidToken" })
            );
            break;
          case 500:
            setServerError(
              intl.formatMessage({ id: "resetPassword.internalServerError" })
            );
            break;
          default:
            setServerError(
              intl.formatMessage({ id: "resetPassword.failedToReset" })
            );
        }
      } else if (error.message) {
        setServerError(error.message);
      } else {
        setServerError(
          intl.formatMessage({ id: "resetPassword.failedToReset" })
        );
      }
    }
    setSubmitting(false);
  };

  return (
    <Box
      sx={{
        my: isMobile ? 4 : 12,
        mx: isMobile ? 2 : 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography component="h1" variant="h5">
        <FormattedMessage id="resetPassword.title" />
      </Typography>
      {serverError && (
        <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
          {serverError}
        </Alert>
      )}
      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting, status }) => (
          <Form noValidate style={{ width: "100%" }}>
            <Field
              as={PasswordField}
              margin="normal"
              required
              fullWidth
              id="password"
              label={intl.formatMessage({ id: "resetPassword.newPassword" })}
              name="password"
              type="password"
              error={touched.password && !!errors.password}
              helperText={touched.password && errors.password}
            />
            <Field
              as={PasswordField}
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              label={intl.formatMessage({
                id: "resetPassword.confirmNewPassword",
              })}
              name="confirmPassword"
              type="password"
              error={touched.confirmPassword && !!errors.confirmPassword}
              helperText={touched.confirmPassword && errors.confirmPassword}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              <FormattedMessage id="resetPassword.resetButton" />
            </Button>
            {status && status.success && (
              <Alert severity="success" sx={{ mt: 2, width: "100%" }}>
                <FormattedMessage id="resetPassword.successMessage" />
              </Alert>
            )}
          </Form>
        )}
      </Formik>
      <Box sx={{ width: "100%", mt: 2 }}>
        <Link to="/auth/signin" style={{ color: "#1976d2", fontSize: "14px" }}>
          <FormattedMessage id="resetPassword.backToSignIn" />
        </Link>
      </Box>
    </Box>
  );
};

export default ResetPassword;
